// @flow
import React, { Component, Fragment, type Node, type ElementRef } from 'react';
import dynamic from 'next/dynamic';
import classnames from 'classnames';
import { get, omit } from 'lodash';

import config from 'Config';
import { addTranslationKeys } from 'Src/i18n';
import { userInfoWrapper } from 'Store/user-info/hoc/UserInfo.jsx';
import ReactAppContext from 'Context/ReactAppContext';
import { CrisisAlertLinkBanner } from 'Components/widgets/crisis-alert/CrisisAlertLinkBanner.jsx';
import { PromotionalBanner } from 'Components/widgets/promotional-banner/PromotionalBanner.jsx';
import { Navigation } from 'Components/widgets/navigation/Navigation.jsx';
import { FooterContainer } from 'Components/layouts/footer/FooterContainer.jsx';
import { DataScore } from 'Components/widgets/data-score/DataScore';

import 'Components/stylesheets/critical.css';

const LazyLayout = dynamic(() => import('./LazyLayout.jsx').then(c => c.LazyLayout), {
  ssr: false,
});

const NavigationBar = userInfoWrapper(Navigation);

type Props = {
  className?: string,
  children?: Node,
  config: CMSContent,
  disableHeader: boolean,
  disableFooter: boolean,
  isMicePage: boolean,
  legalInformations: {
    newsletter: Array<{
      id: number,
      description: string,
    }>,
  },
  newsletters: Array<{
    id: string,
    description: string,
  }>,
};

const getLocales = (currentLocale, availableLocales) => {
  const hiddenLocales = (config.get('featureFlipping.hiddenLocales') || '')
    .split(',')
    .filter(l => l !== currentLocale);
  return omit(availableLocales, hiddenLocales);
};

export class Layout extends Component<Props> {
  static contextType = ReactAppContext;

  promoBannerRef: { current: ElementRef<*> | null };

  constructor(props: Props) {
    super(props);

    this.promoBannerRef = React.createRef();
  }

  render() {
    const {
      config: CMSConfig,
      disableFooter,
      disableHeader,
      className,
      isMicePage,
      legalInformations,
      newsletters,
    } = this.props;
    const { availableLocales, locale: currentLocale } = this.context;

    const {
      account,
      memberSpace,
      navigationBar,
      // TODO but why?
      promotionalBanner: maybePromotionalBanner,
    } = CMSConfig || {}; // sometimes CMSConfig is undefined at first render witch causes error with destructuring ;
    const promotionalBanner = maybePromotionalBanner || {};
    addTranslationKeys(CMSConfig);

    const navLanguage = {
      currentLocale,
      locales: getLocales(currentLocale, availableLocales),
    };

    return (
      <Fragment>
        <LazyLayout />
        {!disableHeader &&
          !isMicePage &&
          promotionalBanner.enabled &&
          (promotionalBanner.title || promotionalBanner.description) && (
            <PromotionalBanner
              bgColor={promotionalBanner.color}
              className="relative z-6"
              closeLabel={promotionalBanner.closeButtonLabel}
              countdown={promotionalBanner.countdown}
              description={promotionalBanner.description}
              disabledPages={promotionalBanner.disabledPages}
              enabledPages={promotionalBanner.enabledPages}
              iconName={promotionalBanner.icon}
              label={promotionalBanner.iconTitle}
              link={{
                url: promotionalBanner.link,
              }}
              title={promotionalBanner.title}
              titleColor={promotionalBanner.titleColor}
              ref={this.promoBannerRef}
            />
          )}
        <div className={classnames(className, 'Layout relative table table-fixed w-full')}>
          {!disableHeader ? (
            <div className="absolute top-0 inset-x-0">
              {get(CMSConfig, 'crisisAlert.enabled') && (
                <div className="absolute top-0 rtl:right-0 w-full z-5">
                  <div className="flex p-5 max-w-site mt-navigation mx-auto">
                    <CrisisAlertLinkBanner
                      alertDate={get(CMSConfig, 'crisisAlert.alertDate', '')}
                      className="rtl:ml-auto"
                      isEnabled={get(CMSConfig, 'crisisAlert.enabled')}
                      linkLabel={get(CMSConfig, 'crisisAlert.link.label')}
                      linkUrl={get(CMSConfig, 'crisisAlert.link.url')}
                    />
                  </div>
                </div>
              )}
              <NavigationBar
                cmsAccount={account}
                cmsMemberSpace={memberSpace}
                language={navLanguage}
                navigation={navigationBar}
                promoBannerRef={this.promoBannerRef}
              />
            </div>
          ) : null}
          {this.props.children}
          {!disableFooter && (
            <FooterContainer
              newsletters={newsletters}
              legalInformations={legalInformations}
              config={CMSConfig}
            />
          )}
        </div>
        <DataScore />
      </Fragment>
    );
  }
}
